/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import type { WithRouterProps } from '@modules/routing/withRouter'
import withRouter from '@modules/routing/withRouter'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals'
import { getStackDeployment } from '@/reducers/stackDeployments'

import type { Params } from 'react-router-dom'
import type { ReduxState } from '@/types/redux'

type StateProps = {
  regionId: string

  // legacy deployment ID, but in reality this is just the ES cluster ID
  deploymentId: string | null

  // actual deployment ID
  stackDeploymentId: string
}

interface DispatchProps {}

export type CommonStackDeploymentRouteParams = {
  issue: string
  regionId: string
  deploymentId: string
}

type ConsumerProps<
  TParams extends Params<string | never> = {}, // eslint-disable-line @typescript-eslint/ban-types
> = WithRouterProps<CommonStackDeploymentRouteParams & TParams>

export type WithStackDeploymentRouteParamsProps<
  TParams extends Params<string | never> = {}, // eslint-disable-line @typescript-eslint/ban-types
> = StateProps & DispatchProps & ConsumerProps<TParams>

/* Under the old API mode, we used the Clusters APIs and /:deploymentId refers to the ES cluster ID
 * In Stack Deployments mode, we use the Deployments API and /:deploymentId refers to the deployment ID
 * This HOC was introduced to make it easy for any top level route to deal with the migration.
 */
const mapStateToProps = (
  state: ReduxState,
  {
    router: {
      params: { regionId: routeRegionId, deploymentId },
    },
  }: WithRouterProps<CommonStackDeploymentRouteParams>,
): StateProps => {
  const stackDeployment = getStackDeployment(state, deploymentId)

  if (!stackDeployment) {
    return {
      regionId: routeRegionId,
      deploymentId: null,
      stackDeploymentId: deploymentId,
    }
  }

  const regionId = getRegionId({ deployment: stackDeployment })
  const esClusterId = stackDeployment.resources.elasticsearch[0]?.id ?? null

  return {
    regionId,
    deploymentId: esClusterId,
    stackDeploymentId: deploymentId,
  }
}

export function withStackDeploymentRouteParams(wrappedComponent) {
  return withRouter(
    connect<StateProps, DispatchProps, ConsumerProps>(mapStateToProps)(wrappedComponent),
  )
}
